import { UseQueryResult } from "@tanstack/react-query";
import { message } from "antd";
import { MenuProps } from "antd/lib";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { Post } from "store/posts/postConstants";
import {
  downloadFile,
  fetchMediaBlob,
  getFileType,
} from "utils/fileDownloadUtils";
import { PostEditingDetails } from "../../PostEditor/PostEditor/postEditorConstants";

interface UsePostCardActionsProps {
  post: Post;
  postDetail: PostEditingDetails;
  isCarousel: boolean;
  isVideo: boolean;
  refetch: () => Promise<UseQueryResult<PostEditingDetails>>;
  setIsDownloading: (value: boolean) => void;
  toggleFlip: () => void;
  setIsFetchingForMenu: (value: boolean) => void;
  setIsMenuOpen: (value: boolean) => void;
  duplicatePost: (post: Post) => void;
}

export default function usePostCardActions({
  post,
  postDetail,
  isCarousel,
  isVideo,
  refetch,
  setIsDownloading,
  toggleFlip,
  setIsFetchingForMenu,
  setIsMenuOpen,
  duplicatePost,
}: UsePostCardActionsProps) {
  const downloadSingleImage = async () => {
    try {
      await downloadFile(
        postDetail?.mediaUrls?.[0],
        isVideo ? "video" : "image"
      );
      message.success(`${isVideo ? "Video" : "Image"} download has started`);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error(
          "Unable to download media, message support for additional assistance"
        );
      }
    }
  };

  const downloadCarousel = async () => {
    if (!postDetail.mediaUrls || postDetail.mediaUrls.length === 0) {
      message.info("Post has no media to download");
      return;
    }

    try {
      const zip = new JSZip();
      const folder = zip.folder("collection")!;

      const promises = postDetail.mediaUrls.map(async (url, i) => {
        const blob = await fetchMediaBlob(url);
        folder.file(`media-${i + 1}.${getFileType(url)}`, blob);
      });

      await Promise.all(promises);

      const content = await folder.generateAsync({ type: "blob" });
      saveAs(content, `carousel-${post.id || "download"}.zip`);
      message.success("Carousel download started");
    } catch (error) {
      console.error("Carousel download error:", error);
      message.error(
        "Unable to download carousel, message support for additional assistance"
      );
    }
  };

  const handleImageDownload = async () => {
    setIsDownloading(true);
    try {
      if (isCarousel) {
        await downloadCarousel();
      } else {
        await downloadSingleImage();
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const goToTemplate = async () => {
    try {
      let templateId = postDetail?.template?.id;

      if (!templateId) {
        const { data: fetchedPost } = await refetch();
        templateId = (fetchedPost as PostEditingDetails)?.template?.id;
      }

      if (templateId) {
        window.open(
          `https://editor.mymarky.ai/?template-id=${templateId}`,
          "_blank"
        );
      } else {
        message.error("Could not find a template associated with this post.");
      }
    } catch (error) {
      console.error("Error navigating to template:", error);
      message.error(
        "Something went wrong finding the template, please try again later"
      );
    }
  };

  const handleMenuClick: MenuProps["onClick"] = async ({ key, domEvent }) => {
    domEvent.stopPropagation();

    switch (key) {
      case "download":
        await handleImageDownload();
        break;
      case "duplicate":
        duplicatePost(post);
        break;
      case "go-to-template":
        await goToTemplate();
        break;
      case "inputs":
        toggleFlip();
        break;
    }
    setIsMenuOpen(false);
  };

  const handleMenuOpenChange = async (open: boolean) => {
    setIsMenuOpen(open);
    if (open && !postDetail?.template?.id) {
      setIsFetchingForMenu(true);
      try {
        await refetch();
      } catch (error) {
        console.error("Failed to fetch post details for menu:", error);
      } finally {
        setIsFetchingForMenu(false);
      }
    }
  };

  return {
    handleMenuClick,
    handleMenuOpenChange,
  };
}
